import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Badge, 
  IconButton, 
  Menu, 
  Typography, 
  Button, 
  Box, 
  List, 
  ListItem, 
  Divider, 
  CircularProgress,
  Tooltip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import dropOffApprovalApi from '../../api/dropOffApprovalApi';
import { getPiiPendingCount } from '../../api/piiAccessApi';
import PiiAccessRequestsModal from '../PiiAccess/PiiAccessRequestsModal';
import workTasksApi from '../../api/workTasksApi';

const AdminNotificationBell = () => {
  const [approvals, setApprovals] = useState([]);
  const [piiCount, setPiiCount] = useState(0);
  const [taskNotices, setTaskNotices] = useState({ items: [], unread_total: 0 });
  const [piiModalOpen, setPiiModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  const open = Boolean(anchorEl);

  const fetchApprovals = async () => {
    try {
      setTaskNotices(await workTasksApi.notices());
    } catch (_) {
      setTaskNotices({ items: [], unread_total: 0 });
    }
    try {
      const data = await dropOffApprovalApi.getPendingApprovals();
      setApprovals(data);
    } catch (err) {
      console.error('Failed to fetch drop-off approvals:', err);
    }
    try {
      const piiData = await getPiiPendingCount();
      setPiiCount(piiData?.count || 0);
    } catch (err) {
      // non-critical
    }
  };

  useEffect(() => {
    fetchApprovals();
    const interval = setInterval(fetchApprovals, 120000);
    window.addEventListener('work-tasks-changed', fetchApprovals);
    return () => { clearInterval(interval); window.removeEventListener('work-tasks-changed', fetchApprovals); };
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    fetchApprovals();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApprove = async (e, id) => {
    e.stopPropagation();
    setActionLoading(id);
    try {
      await dropOffApprovalApi.approve(id);
      setApprovals(prev => prev.filter(a => a.id !== id));
      handleClose();
      window.location.reload();
    } catch (err) {
      console.error('Approve failed:', err);
      alert('Failed to approve: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (e, id) => {
    e.stopPropagation();
    setActionLoading(id);
    try {
      await dropOffApprovalApi.decline(id);
      setApprovals(prev => prev.filter(a => a.id !== id));
      handleClose();
      window.location.reload();
    } catch (err) {
      console.error('Decline failed:', err);
      alert('Failed to decline: ' + (err.response?.data?.detail || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleNavigate = (processId) => {
    handleClose();
    navigate(`/repair-processes/${processId}`);
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    return d.toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const count = approvals.length + piiCount + taskNotices.unread_total;

  return (
    <Box sx={{ position: 'relative', mr: 1 }}>
      <Tooltip title={count > 0 ? `${count} Pending Action${count > 1 ? 's' : ''}` : 'Notifications'}>
        <IconButton
          onClick={handleClick}
          size="large"
          aria-controls={open ? 'notifications-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{
            color: '#32cd32', // Use theme/brand #32cd32
            transition: 'background-color 0.2s',
            '&:hover': {
              backgroundColor: 'rgba(50, 205, 50, 0.08)',
            }
          }}
        >
          <Badge 
            badgeContent={count} 
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                height: '18px',
                minWidth: '18px',
                animation: count > 0 ? 'pulse 2s infinite' : 'none',
              }
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        id="notifications-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            width: '380px',
            maxHeight: '480px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.06)',
            marginTop: '8px',
            padding: 0
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <WarningIcon sx={{ color: '#ff9800', fontSize: '18px' }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
              Drop-Off Approvals
            </Typography>
          </Box>
          {count > 0 && (
            <Badge 
              badgeContent={`${count} pending`} 
              color="warning" 
              sx={{ 
                '& .MuiBadge-badge': { 
                  position: 'static', 
                  transform: 'none', 
                  backgroundColor: '#fef3c7', 
                  color: '#d97706',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: '20px',
                  borderRadius: '6px',
                  px: 1
                } 
              }} 
            />
          )}
        </Box>

        {taskNotices.items.length > 0 && <Box sx={{ p: 1.5 }}>
          <Typography fontWeight={700}>Teamaufgaben ({taskNotices.unread_total} ungelesen)</Typography>
          {taskNotices.items.slice(0, 10).map(notice => <Button key={notice.id} fullWidth sx={{ justifyContent: 'flex-start', fontWeight: notice.read_at ? 400 : 700 }} onClick={async () => {
            try {
              await workTasksApi.readNotice(notice.id);
              handleClose(); navigate(`/admin/pmi-platform/tasks?task=${encodeURIComponent(notice.task_id)}`);
            } catch (_) { setTaskNotices({ items: [], unread_total: 0 }); }
          }}>{notice.title}</Button>)}
        </Box>}
        {/* PII Contact Access Requests banner */}
        {piiCount > 0 && (
          <Box sx={{
            p: 1.5,
            m: 1.5,
            mb: 0.5,
            backgroundColor: '#eff6ff',
            borderRadius: '8px',
            border: '1px solid #bfdbfe'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>🔒</span> Contact Access Requests
              </Typography>
              <Badge
                badgeContent={`${piiCount} pending`}
                sx={{
                  '& .MuiBadge-badge': {
                    position: 'static',
                    transform: 'none',
                    backgroundColor: '#dbeafe',
                    color: '#1d4ed8',
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    height: '18px',
                    borderRadius: '4px',
                    px: 0.8
                  }
                }}
              />
            </Box>
            <Typography sx={{ fontSize: '0.73rem', color: '#3b82f6', mb: 1 }}>
              Drivers requested temporary access to customer phone numbers.
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
                setPiiModalOpen(true);
              }}
              sx={{
                fontSize: '0.72rem',
                py: 0.4,
                px: 1.2,
                textTransform: 'none',
                backgroundColor: '#0284c7',
                '&:hover': { backgroundColor: '#0369a1' }
              }}
            >
              Review Requests
            </Button>
          </Box>
        )}

        {/* List Items */}
        <List sx={{ p: 0, maxHeight: '400px', overflowY: 'auto' }}>
          {count === 0 ? (
            <Box sx={{ py: 4, px: 2, textAlign: 'center', color: '#64748b' }}>
              <CheckCircleIcon sx={{ color: '#10b981', fontSize: '32px', mb: 1, display: 'block', mx: 'auto' }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                No pending approvals
              </Typography>
            </Box>
          ) : (
            approvals.map((a, index) => (
              <React.Fragment key={a.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    px: 2.5,
                    py: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#fefce8', // pale yellow highlight
                    }
                  }}
                  onClick={() => handleNavigate(a.process_id)}
                >
                  {/* Top Row: Repair ID & Time */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      color: '#1d4ed8',
                      backgroundColor: '#eff6ff',
                      px: 1,
                      py: 0.25,
                      borderRadius: '6px',
                    }}>
                      {a.repair_id}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                      {formatTime(a.verified_at)}
                    </Typography>
                  </Box>

                  {/* Stations Info */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <LocationOnIcon sx={{ color: '#16a34a', fontSize: '14px' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Assigned: <span style={{ fontWeight: 600, color: '#14532d' }}>{a.assigned_station_name}</span>
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <LocationOnIcon sx={{ color: '#dc2626', fontSize: '14px' }} />
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Scanned at: <span style={{ fontWeight: 600, color: '#7f1d1d' }}>{a.verifying_station_name}</span>
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={(e) => handleApprove(e, a.id)}
                      disabled={actionLoading === a.id}
                      startIcon={actionLoading === a.id ? <CircularProgress size={12} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{
                        flex: 1,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        py: 0.75,
                        backgroundColor: '#16a34a',
                        textTransform: 'none',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#15803d',
                        }
                      }}
                    >
                      {actionLoading === a.id ? 'Approving...' : 'Approve'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={(e) => handleDecline(e, a.id)}
                      disabled={actionLoading === a.id}
                      startIcon={actionLoading === a.id ? <CircularProgress size={12} color="inherit" /> : <CancelIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{
                        flex: 1,
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        py: 0.75,
                        textTransform: 'none',
                        borderColor: '#fca5a5',
                        '&:hover': {
                          backgroundColor: 'rgba(220, 53, 69, 0.05)',
                          borderColor: '#ef4444',
                        }
                      }}
                    >
                      {actionLoading === a.id ? 'Declining...' : 'Decline'}
                    </Button>
                  </Box>
                </ListItem>
              </React.Fragment>
            ))
          )}
        </List>

        <Box sx={{ p: 1, textAlign: 'center', borderTop: '1px solid #f1f5f9', backgroundColor: '#fafafa' }}>
          <Button
            size="small"
            onClick={() => {
              handleClose();
              setPiiModalOpen(true);
            }}
            sx={{ fontSize: '0.75rem', textTransform: 'none', color: '#475569', fontWeight: 600 }}
          >
            🔒 Manage Customer Contact Requests {piiCount > 0 ? `(${piiCount})` : ''}
          </Button>
        </Box>
      </Menu>

      <PiiAccessRequestsModal
        isOpen={piiModalOpen}
        onClose={() => setPiiModalOpen(false)}
        onActionCompleted={fetchApprovals}
      />

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
      `}</style>
    </Box>
  );
};

export default AdminNotificationBell;
